/**
 * Frontend feature flags.
 *
 * Defaults are chosen so the app is usable while backend endpoints are still in flight.
 */

/**
 * When true, the auditor-feedback **monitoring** client serves in-memory mock data.
 * Defaults to OFF now that `GET /api/auditor-feedback/monitoring` ships (HBAC2-9045).
 */
export const AUDITOR_FEEDBACK_MONITORING_USE_MOCK =
  (import.meta.env.VITE_AUDITOR_FEEDBACK_MONITORING_MOCK ??
    import.meta.env.VITE_AUDITOR_FEEDBACK_MOCK ??
    'false') !== 'false';

/**
 * When true, the auditor-feedback **triggers/replay** client serves in-memory mock data
 * instead of calling `/api/auditor-feedback/triggers*`.
 *
 * Defaults to OFF now that HBAC2-9003 triggers endpoints ship. Set
 * `VITE_AUDITOR_FEEDBACK_MOCK=true` to use mock data.
 */
export const AUDITOR_FEEDBACK_USE_MOCK =
  (import.meta.env.VITE_AUDITOR_FEEDBACK_MOCK ?? 'false') !== 'false';

/**
 * Master switch for the Auditor Feedback feature.
 *
 * When false (the default), the feature is hidden entirely: the top-nav link is
 * not rendered and the `/auditor-feedback/*` routes redirect to `/forms`. Set
 * `VITE_AUDITOR_FEEDBACK_ENABLED=true` (per environment) to expose it. When
 * enabled, visibility is still subject to the `AUDITOR_FEEDBACK_READ` permission.
 *
 * Build-time default (env var). A runtime override stored in localStorage takes
 * precedence over this — see {@link AUDITOR_FEEDBACK_ENABLED} and the hidden
 * `/dev/flags` page.
 */
export const AUDITOR_FEEDBACK_BUILD_ENABLED =
  (import.meta.env.VITE_AUDITOR_FEEDBACK_ENABLED ?? 'false') === 'true';

/** localStorage key holding the runtime override (`'true'` | `'false'` | absent). */
export const AUDITOR_FEEDBACK_OVERRIDE_KEY = 'pulse.flags.auditorFeedbackEnabled';

/**
 * Reads the runtime override set via the hidden `/dev/flags` page.
 * Returns `true`/`false` when an override is set, or `null` to fall back to the
 * build-time default.
 */
export function getAuditorFeedbackOverride(): boolean | null {
  try {
    const value = localStorage.getItem(AUDITOR_FEEDBACK_OVERRIDE_KEY);
    if (value === 'true') return true;
    if (value === 'false') return false;
  } catch {
    // localStorage unavailable (e.g. SSR/private mode) — ignore.
  }
  return null;
}

/**
 * Persists a runtime override for the Auditor Feedback feature. Pass `null` to
 * clear the override and fall back to the build-time default. The effective flag
 * is only re-read on the next module load, so callers should reload the page.
 */
export function setAuditorFeedbackOverride(value: boolean | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(AUDITOR_FEEDBACK_OVERRIDE_KEY);
    } else {
      localStorage.setItem(AUDITOR_FEEDBACK_OVERRIDE_KEY, String(value));
    }
  } catch {
    // localStorage unavailable — ignore.
  }
}

/**
 * Effective Auditor Feedback flag: the runtime override wins, otherwise the
 * build-time default. Evaluated once at module load — the nav link (AppLayout)
 * and the routes (router) both read this, so toggling the override requires a
 * page reload to take effect.
 */
export const AUDITOR_FEEDBACK_ENABLED =
  getAuditorFeedbackOverride() ?? AUDITOR_FEEDBACK_BUILD_ENABLED;
